import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { confirmAlert } from 'react-confirm-alert';
import { OverlayTrigger, Tooltip, Row, Col } from 'react-bootstrap';
import moment from 'moment';

//components
import withRemoveDialog from '../../../../hoc/withRemoveDialog';
import withLocalization from '~/hoc/withLocalization';
import UrgentCallForm from './UrgentCallForm';

//elements
import Button from '~/components/CustomButton/CustomButton.jsx';
import LoadingSpinner from '~/elements/LoadingSpinner';
import Select from '../../../../elements/Select.js';

//utils
import { getIntegrationProduct } from '../../../../utils/integrationUtils.js';
import { translateLabels } from '../../../../utils/commonMethods';

//assets 
import Edit from '~/assets/img/editing.svg';
import Delete from '~/assets/img/deleting.svg';
import Sync from '~/assets/img/Sync.svg'
import AwaitingClock from '~/assets/img/AwaitingClock.svg';

import '../../../../components/Chat/ConversationSearch/ConversationSearch.css';

const urgentCallTypes = [
    { label: 'All', value: '', key: 'All' },
    { label: 'Field Operative', value: true, key: 'Field Operative' },
    { label: 'Customer Care', value: false, key: 'Customer Care' },
]
class UrgentCallList extends Component {
    constructor(props) {
        super(props);
        this.fetchList = props.hasUrgentCallData;
        this.state = {
            showUrgentCallForm: !this.fetchList,
            currentEditingUserId: 0,
            weekNumber: props.weekNumber,
            rows: [],
            isLoading: false,
            syncingUserId: null,
            usersWithUrgentCalls: [],
            filters: {
                type: urgentCallTypes[0].value,
                name: ''
            }
        };
        this.closeUrgentCallForm = this.closeUrgentCallForm.bind(this);
    }

    componentDidMount() {
        if (this.fetchList)
            this.setState({ isLoading: true }, this.loadData);
    }

    loadData = async () => {
        const { urgentCallsStore, commonStore, t, weekNumber, year } = this.props;
        const { filters } = this.state;
        const { urgentWorkSettings } = commonStore?.config?.client?.data?.basicRules;
        try {
            const list = await urgentCallsStore.loadUrgentCallsForWeek(weekNumber, { ...filters, year }, urgentWorkSettings);
            const _usersWithUrgentCalls = list.map(e => e.user_id);
            this.setState({ rows: list, usersWithUrgentCalls: _usersWithUrgentCalls, isLoading: false });
        } catch (error) {
            this.setState({ isLoading: false });
            console.error(error);
            commonStore.addNotification(t('Something went wrong'), null, 'error');
        }
    }


    handleActionButton = (user_id, action) => {
        const { handleDelete } = this.props;
        if (action === 'delete') {
            return handleDelete(user_id, (user_id) => {
                return this.removeUrgentCall(user_id);
            });
        }
        if (action === 'edit' || action === 'add') {
            this.setState({
                showUrgentCallForm: true,
                currentEditingUserId: user_id
            })
        }
    }

    removeUrgentCall = async (user_id) => {
        const { urgentCallsStore, commonStore, t, weekNumber, year } = this.props;
        try {
            await urgentCallsStore.deleteUrgentCallWeek({ userId: user_id, weekNumber: weekNumber, year })
            commonStore.addNotification(t('Success'), null, 'success');
            this.props.reloadData();
        } catch (error) {
            console.error(error);
        }
    }


    handleSyncWithIntegration = async (user_id, action) => {
        const { t, commonStore, urgentCallsStore, weekNumber, year } = this.props;
        const message = action === 'sync'
            ? `${t("Do You want to Sync Urgent work with")} ${commonStore.config?.integration_details?.product}`
            : `${t('Are you sure you want to de-sync the Urgent work from Payroll?')}`;

        confirmAlert({
            title: action === 'reset' ? t(`Confirm to Desync`) : t('Confirm to Sync'),
            message,
            buttons: [
                {
                    label: t('Yes'),
                    onClick: async () => {
                        this.setState({ syncingUserId: user_id })
                        try {
                            await (action === 'sync'
                                ? urgentCallsStore.syncToPayroll(user_id, weekNumber, year)
                                : urgentCallsStore.desyncToPayroll(user_id, weekNumber, year));
                            commonStore.addNotification(`${t('Success')}`, null, 'success');
                            await this.loadData();
                        } catch (error) {
                            commonStore.addNotification(t('Error'), null, 'error');
                            console.error(error);
                        } finally {
                            this.setState({ syncingUserId: null })
                        }
                    },
                },
                {
                    label: t('No'),
                    onClick: () => { },
                },
            ],
        });
    }

    getWeekRange = () => {
        const { weekNumber, year } = this.props;
        const week = moment().isoWeekYear(year || moment().isoWeekYear()).isoWeek(weekNumber);
        return `${week.clone().startOf('isoWeek').format('DD MMM')} - ${week.clone().endOf('isoWeek').format('DD MMM YYYY')}`;
    }

    closeUrgentCallForm = (isReload = false) => {
        if (isReload) {
            this.props.reloadData();
        }
        else this.setState({ currentEditingUserId: 0 });
    }

    setFilters = (name, value) => {
        const { filters } = this.state;
        filters[name] = value;
        this.setState({ filters }, () => {
            if (name === 'type') this.applyFilters();
        })
    }

    applyFilters = () => {
        this.setState({ isLoading: true }, this.loadData);
    }


    render() {
        const { commonStore, t, weekNumber, handleUnsavedChanges, year, userStore } = this.props;
        const { rows, currentEditingUserId, showUrgentCallForm, isLoading, usersWithUrgentCalls, filters, syncingUserId } = this.state;
        if (isLoading)
            return <LoadingSpinner />;
        translateLabels(urgentCallTypes, t, 'label');
        const { extraPayments = [], basicRules = null } = commonStore?.config?.client?.data;
        const allowFieldOperationToSync = basicRules?.urgentWorkSettings?.allowSyncingFieldOperativeUrgentWorkToIntegration;
        const isMultiTaskCustomerCare = extraPayments.some(ep => (ep.code === 'urgent_call' || ep.code === 'urgent_call_weekend') && ep.isMultiTask);
        const isMember = userStore?.currentUser?.user_type === 'member';

        return (
            <>
                {showUrgentCallForm ? <UrgentCallForm
                    userId={currentEditingUserId}
                    weekNumber={weekNumber}
                    usersWithUrgentCalls={usersWithUrgentCalls}
                    onCloseForm={this.closeUrgentCallForm}
                    handleUnsavedChanges={handleUnsavedChanges}
                    year={year}
                />
                    : <div className='m-2'>
                        <Row className='my-2'>
                            <h5 className="m-0 fw-bold" style={{ color: '#2550AC' }}>{t('Urgent Work List')} - {weekNumber} ({this.getWeekRange()})</h5>
                        </Row>
                        <Row>
                            <Col xs={6}>
                                <div className="conversation-search">
                                    <input
                                        type="text"
                                        className="conversation-search-input"
                                        placeholder={t("Search by employee name")}
                                        onChange={(e) => this.setFilters('name', e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                this.applyFilters()
                                            }
                                        }}
                                        value={filters['name']}
                                    />
                                    <i className="fa fa-search conversation-search-icon" onClick={this.applyFilters} />
                                </div>
                            </Col>
                            <Col xs={4}>
                                <Select
                                    options={urgentCallTypes}
                                    value={urgentCallTypes.find(type => type.value === filters.type)}
                                    onChange={(e) => this.setFilters('type', e.value)}
                                />
                            </Col>
                            <Col xs={2} className='d-flex justify-content-end'>
                                <Button wd fill
                                    disabled={isMember}
                                    onClick={() => this.handleActionButton(0, 'add')}
                                >
                                    <i className="fa fa-plus-circle fa-xl margin-right-10" />{t('Add')}
                                </Button>
                            </Col>
                        </Row>

                        <div className="table-responsive">
                            <table className="table">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: '20%' }} className='text-start'>{t('Employee')}</th>
                                        <th style={{ width: '20%' }}>{t('Urgent work')} {t('Type')}</th>
                                        <th style={{ width: '30%' }}>{t('Urgent work')} {t('Days')}</th>
                                        {basicRules?.urgentWorkSettings?.allowStandbyCalls && (
                                            <th style={{ width: '30%' }}>{t('Standby')} {t('Days')}</th>
                                        )}
                                        <th style={{ width: '30%' }} className='text-center'>{t('Actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length ? rows.map((ele, idx) => (
                                        <tr key={`uc_list__${idx}`}>
                                            <td className='text-start'>{ele.employee}</td>
                                            <td className='text-center'>
                                                <span className={'badge bg-secondary px-2 py-1'}>
                                                    {t(ele.type)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-wrap gap-1 justify-content-center">
                                                    {ele.days.map((e, index) => (
                                                        <span key={`uc_list_day_${index}`}
                                                            className={`badge px-2 py-1 ${e.isHoliday ? 'bg-danger' : 'bg-secondary'}`}>
                                                            {t(e.day)}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            {basicRules?.urgentWorkSettings?.allowStandbyCalls && (
                                                <td>
                                                    <div className="d-flex flex-wrap gap-1 justify-content-center">
                                                        {ele.standby_days.map((e, index) => (
                                                            <span key={`uc_list_standby_day_${index}`}
                                                                className={`badge px-2 py-1 ${e.isHoliday ? 'bg-danger' : 'bg-secondary'}`}>
                                                                {t(e.day)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                            )}
                                            <td className="d-flex justify-content-center align-items-center gap-2">
                                                {!isMember && commonStore.config.integration_details.product && (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id="tooltip-top">
                                                                {(!allowFieldOperationToSync && ele.is_field_operative) ?
                                                                    t("Cannot sync field operative urgent work, try from timesheet") :
                                                                    (!ele.isSynced && isMultiTaskCustomerCare) ? t('Syncing multi-task urgent work to payroll is not supported. Please register the hours directly in the timesheet.') :
                                                                        ele.isSynced ? t('Desync from integrations') : ele.isAwaiting ? `${t('Awaiting')} ${t('Status')} ${t('from')} ${commonStore.config?.integration_details?.product}` : `${t('Sync with integration')} ${commonStore.config?.integration_details?.product}`
                                                                }
                                                            </Tooltip>
                                                        }
                                                    >
                                                        {syncingUserId === ele.user_id ? (
                                                            <i className="fa fa-spinner fa-spin fa-fw"></i>
                                                        ) : ele.isSynced ? (
                                                            <img
                                                                src={getIntegrationProduct(commonStore.config.integration_details.product)}
                                                                width={25}
                                                                alt="integration"
                                                                className="m-1"
                                                                onClick={() => this.handleSyncWithIntegration(ele.user_id, 'reset')}
                                                            />
                                                        ) : ele.isAwaiting ?
                                                            (<div className='m-1'>
                                                                <img src={AwaitingClock} alt="clock" />
                                                            </div>)
                                                            : ( <Button icon_sm fill
                                                                    disabled={(!allowFieldOperationToSync && ele.is_field_operative) || isMultiTaskCustomerCare}
                                                                    onClick={() => this.handleSyncWithIntegration(ele.user_id, 'sync')}
                                                                >
                                                                    <img src={Sync} alt="sync" width={18} />
                                                                </Button>
                                                            )}
                                                    </OverlayTrigger>
                                                )}

                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id="tooltip-top">
                                                            {t('Update')} {t('Urgent work')}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <Button icon_sm fill
                                                        onClick={() => this.handleActionButton(ele.user_id, 'edit')}
                                                    >
                                                        <img src={Edit} alt={'edit'} />
                                                    </Button>
                                                </OverlayTrigger>
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={
                                                        <Tooltip id="tooltip-top">
                                                            {t('Remove')} {t('Weekly')} {t('Urgent work')}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <Button icon_sm_delete fill
                                                        onClick={() => this.handleActionButton(ele.user_id, 'delete')}
                                                    >
                                                        <img src={Delete} alt="delete" />
                                                    </Button>
                                                </OverlayTrigger>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={4} className='text-center font-size-14'>{t('No Data Found')}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                }
            </>
        );
    }
}

export default inject('commonStore', 'urgentCallsStore', 'scheduleStore', 'userStore')(withLocalization(withRemoveDialog(observer(UrgentCallList))));